import { type MouseEvent, useState } from 'react'

import { chartColors, cn, numberToCurrency } from '@/utils'

export interface ValuePoint {
  key: string
  label: string
  value: number
}

const TREND_GRADIENT_ID = 'value-trend-fill'

const clampPercent = (value: number) => Math.min(88, Math.max(12, value))

// Pad the value band so the lowest point doesn't sit on the floor (reads as zero) or the highest
// on the ceiling. The line still shows the full shape of the move.
const VERTICAL_PADDING = 0.18

// Place each point in [0,1] fractions so the SVG path and the HTML dot overlay line up without
// measuring the element. x is evenly spaced, y is inverted so a higher value sits higher.
const toFractions = (values: number[]) => {
  const lowest = Math.min(...values)
  const highest = Math.max(...values)
  const span = highest - lowest || 1

  return values.map((value, index) => {
    const normalized = (value - lowest) / span
    return {
      x: index / (values.length - 1),
      y: VERTICAL_PADDING + (1 - normalized) * (1 - 2 * VERTICAL_PADDING),
    }
  })
}

// Total value over time in one neutral color. Hovering reads out the period and value. Fewer than
// two points has no shape to draw, so it renders nothing.
export const ValueTrendChart = ({ points }: { points: ValuePoint[] }) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  if (points.length < 2) return null

  const width = 320
  const height = 64
  const values = points.map((point) => point.value)
  const fractions = toFractions(values)

  const line = fractions
    .map(({ x, y }, index) => {
      const command = index === 0 ? 'M' : 'L'
      return `${command}${(x * width).toFixed(1)},${(y * height).toFixed(1)}`
    })
    .join(' ')
  const area = `${line} L${width},${height} L0,${height} Z`

  // With no hover, emphasize the latest point, the snapshot the headline number reports.
  const emphasized = hoverIndex ?? points.length - 1
  const firstLabel = points[0].label
  const lastLabel = points[points.length - 1].label

  const handleMove = (event: MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect()
    const ratio = (event.clientX - bounds.left) / bounds.width
    const nearest = Math.round(ratio * (points.length - 1))
    setHoverIndex(Math.min(points.length - 1, Math.max(0, nearest)))
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className="relative h-16 w-full"
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIndex(null)}
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={`Value from ${firstLabel} to ${lastLabel}`}
          className="h-16 w-full"
        >
          <defs>
            <linearGradient id={TREND_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={chartColors.blue}
                stopOpacity={0.25}
              />
              <stop
                offset="100%"
                stopColor={chartColors.blue}
                stopOpacity={0}
              />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#${TREND_GRADIENT_ID})`} />
          <path
            d={line}
            fill="none"
            stroke={chartColors.blue}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {fractions.map((fraction, index) => (
          <span
            key={points[index].key}
            className={cn(
              'absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-sm transition-all',
              index === emphasized ? 'h-3 w-3' : 'h-2 w-2 opacity-80',
            )}
            style={{
              left: `${fraction.x * 100}%`,
              top: `${fraction.y * 100}%`,
              backgroundColor: chartColors.blue,
            }}
          />
        ))}

        {hoverIndex !== null && (
          <div
            className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-md bg-blue-7 px-2 py-1 text-center text-white shadow-sm"
            style={{
              left: `${clampPercent(fractions[hoverIndex].x * 100)}%`,
              top: `${fractions[hoverIndex].y * 100}%`,
              marginTop: '-10px',
            }}
          >
            <div className="text-xs font-semibold tabular-nums">
              {numberToCurrency(points[hoverIndex].value)}
            </div>
            <div className="text-[10px] opacity-80">
              {points[hoverIndex].label}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between text-[11px] text-muted-foreground">
        <span>{firstLabel}</span>
        <span>{lastLabel}</span>
      </div>
    </div>
  )
}
